import { useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import { heritageColors } from '../../../../heritageTheme.ts';
import { useFontLoader } from '../../../../dummies/RichTextEditor/useFontLoader';

const Sticky = ({ data = { notices: [] }, title = 'לוח מודעות', showTitle = true, scale = 1 }) => {
  const contentRef = useRef(null);
  const containerRef = useRef(null);
  const originalContentRef = useRef(null);
  const firstSetRef = useRef(null);
  const [needsScroll, setNeedsScroll] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const notices = (data.notices || []).filter(
    n => !n.expiryDate || new Date(n.expiryDate) > now
  );

  // טעינת פונטים מכל המודעות
  const allNoticesHtml = notices.map(n => n.content).join(' ');
  useFontLoader(allNoticesHtml);

  useEffect(() => {
    const checkScroll = () => {
      if (!containerRef.current || !firstSetRef.current || notices.length === 0) return;
      const containerHeight = containerRef.current.clientHeight;
      const contentHeight = firstSetRef.current.offsetHeight;
      setNeedsScroll(containerHeight > 0 && contentHeight > containerHeight + 2);
    };

    // מחכים שה-DOM יתעדכן לפני הבדיקה
    const timer = setTimeout(checkScroll, 100);

    const resizeObserver = new ResizeObserver(checkScroll);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [notices]);

  return (
    <Box sx={{ p: 3, direction: 'rtl', height: '100%', width: '100%', display: 'flex', flexDirection: 'column', fontSize: 'inherit' }}>
      {/* Header */}
      {showTitle && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <CampaignIcon sx={{ color: heritageColors.navy, fontSize: `${20 / scale}px` }} />
          <Typography variant="h5" sx={{ color: heritageColors.navy, fontWeight: 700, fontSize: `${20 / scale}px` }}>
            {title}
          </Typography>
        </Box>
      )}

      {/* Notices */}
      <Box ref={containerRef} sx={{ overflow: 'hidden', flex: 1, position: 'relative', width: '100%' }}>
        {notices.length === 0 ? (
          <Typography sx={{ textAlign: 'center', color: heritageColors.slate, fontSize: '0.9em', p: 2 }}>
            אין הודעות כרגע
          </Typography>
        ) : (
          <>
            {needsScroll && (
              <style>
                {`
                    @keyframes scrollNotices {
                      0% { transform: translateY(0); }
                      100% { transform: translateY(calc(-100% / 3)); }
                    }
                  `}
              </style>
            )}
            <div ref={contentRef} style={{
              animation: needsScroll ? 'scrollNotices 30s linear infinite' : 'none'
            }}>
              <div ref={originalContentRef} style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                <div ref={firstSetRef} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {notices.map((notice, index) => (
                  <Paper
                    key={notice.id || index}
                    data-notice-item
                    elevation={0}
                    sx={{
                      p: `${12 / scale}px`,
                      borderRadius: '1.25em',
                      border: `0.03125em solid ${heritageColors.border}`,
                    }}
                  >
                    <Typography
                      sx={{
                        color: heritageColors.navy,
                        fontSize: '1em',
                        lineHeight: 1,
                        '& *': {
                          textAlign: 'inherit'
                        }
                      }}
                      dangerouslySetInnerHTML={{ __html: notice.content }}
                    />
                  </Paper>
                ))}
                </div>
                {needsScroll && (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {notices.map((notice, index) => (
                      <Paper
                        key={`dup1-${notice.id || index}`}
                        elevation={0}
                        sx={{
                          p: `${12 / scale}px`,
                          borderRadius: '1.25em',
                          border: `0.03125em solid ${heritageColors.border}`,
                        }}
                      >
                        <Typography
                          sx={{
                            color: heritageColors.navy,
                            fontSize: '1em',
                            lineHeight: 1,
                            '& *': {
                              textAlign: 'inherit'
                            }
                          }}
                          dangerouslySetInnerHTML={{ __html: notice.content }}
                        />
                      </Paper>
                    ))}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {notices.map((notice, index) => (
                      <Paper
                        key={`dup2-${notice.id || index}`}
                        elevation={0}
                        sx={{
                          p: `${12 / scale}px`,
                          borderRadius: '1.25em',
                          border: `0.03125em solid ${heritageColors.border}`,
                        }}
                      >
                        <Typography
                          sx={{
                            color: heritageColors.navy,
                            fontSize: '1em',
                            lineHeight: 1,
                            '& *': {
                              textAlign: 'inherit'
                            }
                          }}
                          dangerouslySetInnerHTML={{ __html: notice.content }}
                        />
                      </Paper>
                    ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Sticky;
